import { Box, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import timesheetNoData from "assets/images/nodata/timesheetNoData.png";
import { useGetReportTableData, useGlobalStore } from "controller";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import type { WorkHoursByTechRow } from "shared";
import { useDashboardScope } from "../../../hooks/useDashboardScope";
import { activityPeriodAtom } from "../../../store";
import type { TimesheetMetric } from "../../../types";
import { ActivityCard } from "./ActivityCard";
import { ActivityEmptyState } from "./ActivityEmptyState";

/**
 * The hour fields come back pre-formatted ("2h 30m"), so they have to be parsed
 * back into minutes before they can be summed. A bare decimal ("12.50") is
 * accepted too, in case the report ever answers with raw hours again.
 */
const toMinutes = (value: string | undefined) => {
  if (!value) return 0;

  const parts = value.match(/-?\d+(?:\.\d+)?\s*[hm]/gi);
  if (parts) {
    return parts.reduce((total, part) => {
      const amount = Number.parseFloat(part);
      return (
        total + (part.trim().toLowerCase().endsWith("m") ? amount : amount * 60)
      );
    }, 0);
  }

  const hours = Number(value);
  return Number.isFinite(hours) ? hours * 60 : 0;
};

/** "2h 30m", dropping the minutes once there are none left to show. */
const formatMinutes = (minutes: number) => {
  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const remainder = rounded % 60;
  return remainder ? `${hours}h ${remainder}m` : `${hours}h`;
};

export const TimesheetsCard = () => {
  const theme = useTheme();
  const timezone = useGlobalStore((state) => state.timezone);
  const period = useAtomValue(activityPeriodAtom);
  const { reportResource, isReady } = useDashboardScope();

  const metricColors: Record<string, string | undefined> = {
    logged: theme.palette.primitive.system.green[600],
    billable: theme.palette.primitive.brand.purple[500],
    non_billable: theme.palette.primitive.system.blue[500],
  };

  // Held until the scope resolves — firing early would show tenant-wide totals
  // for a beat before the scoped numbers replace them.
  const { data, isFetching } = useGetReportTableData(
    {
      reportName: "work-hours-by-techs",
      serviceType: "work hour",
      timePeriod: period,
      timezone,
      resource: reportResource,
      serviceTypeFilter: "ticket",
      filterAvailable: ["Resource"],
    },
    isReady,
  );

  // The report returns one row per resource in the selection, and work hours are
  // genuinely additive (unlike ticket counts), so summing the rows is correct.
  const { metrics, totalMinutes } = useMemo(() => {
    const rows: WorkHoursByTechRow[] = Array.isArray(data) ? data : [];
    const sum = (key: keyof WorkHoursByTechRow) =>
      rows.reduce((total, row) => total + toMinutes(row[key] ?? undefined), 0);

    const logged = sum("total_hours");
    const billable = sum("billing_hours");
    const nonBillable = sum("non_billing_hours");

    const list: TimesheetMetric[] = [
      { id: "logged", label: "Logged hours", value: formatMinutes(logged) },
      {
        id: "billable",
        label: "Billable hours",
        value: formatMinutes(billable),
      },
      {
        id: "non_billable",
        label: "Non-billable hours",
        value: formatMinutes(nonBillable),
      },
    ];

    return { metrics: list, totalMinutes: logged + billable + nonBillable };
  }, [data]);

  // Three zeroed rows read as a broken card rather than an idle period, so the
  // card swaps them for the empty state. Held until the scope resolves.
  const isEmpty = !isFetching && isReady && totalMinutes === 0;

  if (isEmpty) {
    return (
      <ActivityCard title="Time summary">
        <ActivityEmptyState
          image={timesheetNoData}
          title="No hours logged"
          description="No time entries have been recorded."
          imageWidth="75px"
        />
      </ActivityCard>
    );
  }

  return (
    <ActivityCard title="Time summary">
      <Stack sx={{ gap: "16px" }}>
        {metrics.map((metric) => (
          <Stack
            key={metric.id}
            direction="row"
            sx={{ alignItems: "center", gap: "8px" }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: "10px",
                height: "10px",
                borderRadius: "2px",
                backgroundColor:
                  metricColors[metric.id] ?? theme.palette.neutral["020"],
              }}
            />
            <Typography
              variant="body-small"
              sx={{ flex: 1, minWidth: 0, color: theme.palette.lightBg.high }}
              noWrap
            >
              {metric.label}
            </Typography>
            {isFetching ? (
              <Skeleton variant="text" width={32} />
            ) : (
              <Typography
                variant="body-small"
                sx={{ color: theme.palette.lightBg.medium }}
              >
                {metric.value}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    </ActivityCard>
  );
};
